import React from 'react';
import './leaderboard.scss';
import SessionCache from '../../routes/session-cache';
import { Redirect, Router, Switch } from 'react-router-dom'
// import MeritApi from '../../../apis/merit-api';
import SchoolApi from '../../../apis/school'
import Dropdown from '../../common/dropdown-v2/dropdown';
import DropdownV4 from '../../common/dropdown-v4/dropdown';
import CommonApi from '../../../apis/common';
import { getImageSource } from '../../../utility/image-source';
import { Constants, ImageConstants } from '../../../constants/constants';
import Path from '../../routes/routes-path';
import ExcelJS from "exceljs";
import LeaderboardReport from '../leaderboard_report.js/leaderboard_report';

const CATEGORIES = [
    { id: 'top_performer', label: 'Top Performer', icon: '🥇' },
    { id: 'self_learning', label: 'Self Learning Leader', icon: '🚀' },
    { id: 'consistency', label: 'Consistency Champion', icon: '🔥' },
    { id: 'rising_star', label: 'Rising Star', icon: '📈' }
];



const formatLastUpdated = () => {
    try {
        const date = new Date();
        date.setDate(date.getDate() - 1);
        const options = {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            timeZone: 'Asia/Kolkata'
        };
        return date.toLocaleDateString('en-IN', options);

    } catch (e) {
        return '';
    }
};

const formatMonthNavigatorLabel = (date) => {
    const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    return `${months[date.getMonth()]} ${date.getFullYear()}`;
};

const TabBar = ({ activeCategory, onCategoryChange }) => {
    return (
        <div className="leaderboard-tab-bar">
            {CATEGORIES.map(cat => (
                <button
                    key={cat.id}
                    className={`tab-btn ${activeCategory === cat.id ? 'active' : ''}`}
                    onClick={() => onCategoryChange(cat.id)}
                >
                    <span className="tab-icon">{cat.icon}</span>
                    <span className="tab-label">{cat.label}</span>
                </button>
            ))}
        </div>
    );
};

const MonthNavigator = ({ currentMonthDate, onMonthChange, isCurrentMonth }) => {
    const label = formatMonthNavigatorLabel(currentMonthDate);
    return (
        <div className="month-navigator">
            <button className="nav-arrow prev" onClick={() => onMonthChange('prev')}>&larr;</button>
            <div className="month-label">{label}</div>
            <button
                className={`nav-arrow next ${isCurrentMonth ? 'disabled' : ''}`}
                onClick={() => onMonthChange('next')}
                disabled={isCurrentMonth}
            >
                &rarr;
            </button>
        </div>
    );
};

const LeaderboardHeader = ({ lastUpdated, currentMonthDate, onMonthChange, isCurrentMonth, onDownloadReport }) => {
    const timestampStr = formatLastUpdated();
    return (
        <div className="leaderboard-header-bar">
            <div className="header-info">
                <div className="d-flex align-items-center">
                    <h3 className="header-title mb-0">Class Rankings</h3>
                    <div className='cursor-pointer ml-3' onClick={onDownloadReport} title="Export to Excel">
                        <img className='report-icon export-icon' src={getImageSource(ImageConstants.EXPORT_TO_EXCEL)} />
                    </div>
                </div>
                {timestampStr && <span className="header-timestamp">Last updated: {timestampStr}</span>}
            </div>
            <MonthNavigator
                currentMonthDate={currentMonthDate}
                onMonthChange={onMonthChange}
                isCurrentMonth={isCurrentMonth}
            />
        </div>
    );
};

const ColumnHeader = () => {
    return (
        <div className="column-header">
            <div className="col-rank">Rank</div>
            <div className="col-student">Student</div>
            <div className="col-merits">Merits</div>
        </div>
    );
};

const RankBadge = ({ variant, rank }) => {
    return (
        <div className={`rank-badge badge-${variant}`}>
            {rank || '-'}
        </div>
    );
};

const PodiumRow = ({ entry, variant, isCurrentUser, isCurrentMonth }) => {
    // Always highlight the current user regardless of which month is being viewed
    const applyUserHighlight = isCurrentUser;
    const rowClass = `lb-row lb-rank-${variant} ${applyUserHighlight ? 'is-user-podium' : ''}`;

    return (
        <div className={rowClass}>
            <div className="col-rank">
                <RankBadge variant={variant} rank={entry.rank} />
            </div>
            <div className="col-student">
                <span className="student-name">{entry.name}</span>
                {applyUserHighlight && <span className="student-sublabel">You</span>}
            </div>
            <div className="col-merits">{entry.merits}</div>
        </div>
    );
};

const YouRow = ({ entry }) => {
    return (
        <div className="lb-row lb-rank-you">
            <div className="col-rank">
                <RankBadge variant="you" rank={entry.rank} />
            </div>
            <div className="col-student">
                <span className="student-name">{entry.name}</span>
                <span className="student-sublabel">You · Rank {entry.rank || '-'}</span>
            </div>
            <div className="col-merits">{entry.merits}</div>
        </div>
    );
};

const OtherRow = ({ entry, index }) => {
    const alternatingClass = index % 2 === 0 ? 'lb-rank-plain-even' : 'lb-rank-plain-odd';
    return (
        <div className={`lb-row ${alternatingClass}`}>
            <div className="col-rank">
                <RankBadge variant="plain" rank={entry.rank} />
            </div>
            <div className="col-student">
                <span className="student-name">{entry.name}</span>
            </div>
            <div className="col-merits">{entry.merits}</div>
        </div>
    );
};

const SkeletonRow = () => {
    return (
        <div className="skeleton-row">
            <div className="skeleton-badge"></div>
            <div className="skeleton-text"></div>
            <div className="skeleton-merits"></div>
        </div>
    );
};

const getPodiumVariant = (rank, index) => {
    if (rank === 1) return 'gold';
    if (rank === 2) return 'silver';
    if (rank === 3) return 'bronze';
    const fallback = ['gold', 'silver', 'bronze'];
    return fallback[index] || 'gold';
};

const LeaderboardBody = ({
    podium,
    others,
    student,
    showYouRow,
    isLoading,
    isNavigating,
    isError,
    onRetry,
    isCurrentMonth
}) => {
    if (isLoading) {
        return (
            <div className="leaderboard-body-list">
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
            </div>
        );
    }

    if (isError) {
        return (
            <div className="state-container">
                <div className="state-icon">⚠️</div>
                <div className="state-title">Unable to load rankings</div>
                <div className="state-subtitle">There was an error loading the leaderboard data. Please try again.</div>
                <button className="btn-retry" onClick={onRetry}>Retry</button>
            </div>
        );
    }

    if (podium.length === 0 && others.length === 0 && !showYouRow) {
        return (
            <div className="state-container">
                <div className="state-icon">🏆</div>
                <div className="state-title">No data available for this month</div>
                <div className="state-subtitle">Complete activities to appear on the leaderboard!</div>
            </div>
        );
    }

    return (
        <div className={`leaderboard-body-list ${isNavigating ? 'is-navigating' : ''}`}>
            <div className="leaderboard-scroll-body">
                {podium.map((entry, idx) => {
                    const variant = getPodiumVariant(entry.rank, idx);
                    return (
                        <PodiumRow
                            key={`podium-${entry.userId || idx}`}
                            entry={entry}
                            variant={variant}
                            isCurrentUser={entry.isCurrentUser}
                            isCurrentMonth={isCurrentMonth}
                        />
                    );
                })}

                {showYouRow && student && <YouRow entry={student} />}

                {others.map((entry, idx) => (
                    <OtherRow
                        key={`other-${entry.userId || idx}`}
                        entry={entry}
                        index={idx}
                    />
                ))}
            </div>
        </div>
    );
};

export default class Leaderboard extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            activeMainTab: 'leaderboard',
            activeCategory: 'top_performer',
            currentMonthDate: (() => {
                const today = new Date();
                return new Date(today.getFullYear(), today.getMonth(), 1);
            })(),
            leaderboardData: [],
            lastUpdated: null,
            isLoading: true,
            isNavigating: false,
            isError: false,
            boardsDropdownItems: [],
            classDropdownItems: [],
            selectedBoard: '',
            selectedClass: ''
        };
    }

    classSectionHash = {}
    sectionHash = {}

    componentDidMount() {
        // this.fetchClassStudents();
        this.getClasses()
    }


    getClasses(payload = {}) {

        payload.type = this.props.type

        SchoolApi.getClassAndBoard(payload)
            .then(school => {
                // console.log(school)
                let classes = [], boards = [];
                for (let i = 0; i < school.classes.length; i++) {
                    classes.push({
                        title: school.classes[i].name,
                        value: school.classes[i].name,
                    })
                }

                for (let i = 0; i < school.boards.length; i++) {
                    boards.push({
                        title: school.boards[i].name,
                        value: school.boards[i].name,
                    })
                }

                if (boards.length > 1) {
                    this.setState({ boardsDropdownItems: boards })
                } else {
                    this.setState({ selectedBoard: school?.boards[0]?.name })
                }
                this.setState({ classDropdownItems: classes, })
            }).catch(err => {
            })
    }

    fetchClassStudents = (category, monthDate) => {
        let userInfo = SessionCache.getUser();
        let activeCategory = category || this.state.activeCategory;
        let activeMonthDate = monthDate || this.state.currentMonthDate;

        // If it is the first load (no data yet), set isLoading to true. Otherwise, set isNavigating to true.
        const isFirstLoad = this.state.leaderboardData.length === 0;
        if (isFirstLoad) {
            this.setState({ isLoading: true, isError: false });
        } else {
            this.setState({ isNavigating: true, isError: false });
        }

        if (userInfo) {
            const categoryMap = {
                'top_performer': 'Top Performer',
                'self_learning': 'Self Learning Leader',
                'consistency': 'Consistency Champion',
                'rising_star': 'Rising Star'
            };
            const apiCategory = categoryMap[activeCategory] || 'Top Performer';

            if (activeCategory === 'consistency' || activeCategory === 'rising_star') {
                this.setState({
                    leaderboardData: [],
                    lastUpdated: null,
                    isLoading: false,
                    isNavigating: false,
                    isError: false
                });
                return;
            }

            const year = activeMonthDate.getFullYear();
            const monthStr = String(activeMonthDate.getMonth() + 1).padStart(2, '0');
            const apiMonth = `${year}-${monthStr}`;

            let payload = {
                userId: userInfo.userId || userInfo._id,
                category: apiCategory,
                month: apiMonth,
                subjectId: userInfo.subjectId || null,
                selectedBoard: this.state.selectedBoard,
                selectedClass: this.state.selectedClass,
                selectedSection: this.state.selectedSection
            };

            CommonApi.getStudentMeritLeaderboard(payload).then(response => {
                if (response && response.status === 1) {
                    let formattedData = response.data.map(item => {
                        let merits = item.finalMerits ?? 0;
                        if (apiCategory === 'Self Learning Leader') {
                            merits = item.selfLearningMeritsAtRank ?? item.selfLearningMerits ?? merits;
                        } else if (apiCategory === 'Top Performer') {
                            merits = item.totalActivityMeritsAtRank ?? item.totalActivityMerits ?? merits;
                        }

                        return {
                            rank: item.rank ?? null,
                            name: item.name || "Unknown Student",
                            merits: merits,
                            userId: item.userId,
                            isCurrentUser: item.isCurrentUser || false
                        };
                    });

                    formattedData.sort((a, b) => {
                        if (a.rank === null && b.rank === null) return 0;
                        if (a.rank === null) return 1;
                        if (b.rank === null) return -1;
                        return a.rank - b.rank;
                    });

                    this.setState({
                        leaderboardData: formattedData,
                        lastUpdated: response.lastUpdated || null,
                        showLeaderBoard: true,
                        isLoading: false,
                        isNavigating: false
                    });
                } else {
                    this.setState({
                        leaderboardData: [],
                        lastUpdated: null,
                        isLoading: false,
                        isNavigating: false
                    });
                }
            }).catch(err => {
                console.error("Error fetching leaderboard", err);
                this.setState({ isLoading: false, isNavigating: false, isError: true });
            });
        } else {
            this.setState({ isLoading: false, isNavigating: false, isError: true });
        }
    }

    handleMonthChange = (direction) => {
        const today = new Date();
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth();

        const currentSel = this.state.currentMonthDate;
        let newSel;

        if (direction === 'prev') {
            newSel = new Date(currentSel.getFullYear(), currentSel.getMonth() - 1, 1);
            this.setState({ currentMonthDate: newSel }, () => {
                this.fetchClassStudents();
            });
        } else if (direction === 'next') {
            newSel = new Date(currentSel.getFullYear(), currentSel.getMonth() + 1, 1);
            if (newSel <= new Date(currentYear, currentMonth, 1)) {
                this.setState({ currentMonthDate: newSel }, () => {
                    this.fetchClassStudents();
                });
            }
        }
    }

    handleCategoryChange = (category) => {
        this.setState({ activeCategory: category }, () => {
            this.fetchClassStudents();
        });
    }

    onSelectDropDown = (option, key) => {
        console.log(option, key)

        this.setState({ [key]: option?.value }, () => {
            this.props.delegate && this.props.delegate.resetErrorMessages && this.props.delegate.resetErrorMessages()

            // if (key == 'selectedSubSubjectId') {
            //     this.setState({ subSubjectName: option?.title, selectedChapterKey: [], selectedConcept: [] }, () => {
            //         this.resetDropDownsForRevision()
            //     })
            // }

            if (key == "selectedBoard") {
                this.setState({ selectedSection: '', selectedClass: '', reloadDropdown: true }, () => {
                    this.setState({ reloadDropdown: false })
                })
            }

            if (key == "selectedClass" || key == "selectedBoard") {
                if (option?.value) {
                    this.getSections()
                }
            }

            if (key == 'selectedSection') {
                // console.log(option?.title)
                this.setState({ [key]: option?.sectionId, sectionTitle: option?.title }, () => {
                    this.fetchClassStudents()
                })
            }

            // this.emitChanges(key == "selectedClass" || key == "selectedBoard" || key == "selectedSection" || key == 'selectedSubSubjectId')
        })
    }

    getSections(payload = {}) {

        if (!payload.class) payload.class = this.state.selectedClass
        if (!payload.board) payload.board = this.state.selectedBoard

        if (!payload.class || !payload.board) {
            this.setState({ sectionDropdownItems: [], selectedSection: "" })
            // this.reloadDropdown()
            return
        }

        SchoolApi.getSections(payload)
            .then(response => {
                let sections = []
                console.log(response)
                for (let i = 0; i < response.response.length; i++) {

                    if (response.response[i].section !== 'No Sections') {
                        sections.push({
                            title: response.response[i].section,
                            value: response.response[i]._id,
                            sectionId: response.response[i]._id
                        })

                        this.sectionHash[response.response[i]._id] = response.response[i].section
                    }


                }
                this.classSectionHash[payload.class] = sections
                console.log(sections)
                this.setState({ sectionDropdownItems: sections, selectedSection: "", reloadSectionDropdown: true }, () => {
                    this.setState({ reloadSectionDropdown: false })
                })

            }).catch(err => {
                this.setState({ sectionDropdownItems: [], selectedSection: "" })

            })
    }


    onSelectSection = (options, isMannual = false) => {
        let values = options.map(option => option?.value)

        this.setState({ selectedSection: values }, () => {
            this.setState({})
            // this.emitChanges(true)
        })
    }

    onClickBack() {
        return window.location.hash = '#/school/dashboard'
    }

    downloadMeritLeaderboardExcelReport = async () => {
        const { leaderboardData, activeCategory, selectedBoard, selectedClass, selectedSection, sectionDropdownItems } = this.state;
        if (!leaderboardData || leaderboardData.length === 0) return;

        const categoryMap = {
            'top_performer': 'Top Performer',
            'self_learning': 'Self Learning Leader',
            'consistency': 'Consistency Champion',
            'rising_star': 'Rising Star'
        };
        const categoryName = categoryMap[activeCategory] || 'Top Performer';
        const sectionItem = sectionDropdownItems ? sectionDropdownItems.find(s => s.value === selectedSection) : null;
        const sectionName = sectionItem ? sectionItem.title : "-";

        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet("Leaderboard");

        // Header
        sheet.mergeCells("A1:F1");
        sheet.getCell("A1").value = `Class Rankings - ${categoryName}`;
        sheet.getCell("A1").font = { bold: true, size: 14 };
        sheet.getCell("A1").alignment = { horizontal: "center" };

        // Table header
        const headerRow = sheet.addRow(["Student Name", "Board", "Class", "Section", "Rank", "Merits"]);
        headerRow.font = { bold: true, size: 14 };
        headerRow.eachCell((cell) => {
            cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFB7D6F0" }
            };
        });

        // Fill rows with leaderboard data
        leaderboardData.forEach((item) => {
            sheet.addRow([
                item.name || "-",
                selectedBoard || "-",
                selectedClass || "-",
                sectionName,
                item.rank || "-",
                item.merits || 0
            ]);
        });

        // Generate Excel buffer
        const buffer = await workbook.xlsx.writeBuffer();

        // Trigger download
        const blob = new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        const fileNameCategory = (activeCategory || 'top_performer').replace(/_/g, '-');
        const now = new Date();
        const formattedDate = now.toISOString().split('T')[0];
        const formattedTime = now.toTimeString().split(' ')[0].replace(/:/g, '-');
        link.download = `merit_leaderboard_${fileNameCategory}_${formattedDate}_${formattedTime}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
    }

    render() {
        const {
            leaderboardData,
            isLoading,
            isNavigating,
            isError,
            activeCategory,
            currentMonthDate,
            lastUpdated
        } = this.state;

        const today = new Date();
        const isCurrentMonth = currentMonthDate.getFullYear() === today.getFullYear() &&
            currentMonthDate.getMonth() === today.getMonth();

        // Show ALL students with rank 1, 2, or 3 — do NOT slice(0,3) because
        // ties (e.g. two rank-1 students) would push the actual rank-3 student off the podium
        const podium = leaderboardData.filter(item => item.rank !== null && item.rank <= 3);
        const student = leaderboardData.find(item => item.isCurrentUser) || null;

        // Show YouRow whenever the current user is outside the top 3 (regardless of current or past month)
        // This ensures they are never invisible when navigating past months
        const showYouRow = !!(student && (student.rank === null || student.rank > 3));

        // Always exclude the current user from the "others" list — they're shown via YouRow instead
        const others = leaderboardData.filter(item => (item.rank === null || item.rank > 3) && !item.isCurrentUser);

        return (
            <div className="leaderboard-container container">
                {this.state.showBackButton || Constants.ELECTRON_BUILD ? <img className='report-icon cursor-pointer' src={getImageSource(ImageConstants.BACK_ICON)} onClick={this.onClickBack} /> : ''}
                <div className="leaderboard-title-wrapper">
                    <h2 className="leaderboard-title">Students Merit Leaderboard</h2>
                </div>

                <div className="main-tabs-container mb-4">
                    <ul className="nav nav-tabs">
                        <li className="nav-item">
                            <a
                                className={`nav-link ${this.state.activeMainTab === 'leaderboard' ? 'active font-weight-bold' : 'cursor-pointer text-secondary'}`}
                                onClick={() => this.setState({ activeMainTab: 'leaderboard' })}
                                style={{ borderBottom: this.state.activeMainTab === 'leaderboard' ? '2px solid #19C2D1' : 'none' }}
                            >
                                Leaderboard
                            </a>
                        </li>
                        <li className="nav-item">
                            <a
                                className={`nav-link ${this.state.activeMainTab === 'report' ? 'active font-weight-bold' : 'cursor-pointer text-secondary'}`}
                                onClick={() => this.setState({ activeMainTab: 'report' })}
                                style={{ borderBottom: this.state.activeMainTab === 'report' ? '2px solid #19C2D1' : 'none' }}
                            >
                                Leaderboard Report
                            </a>
                        </li>
                    </ul>
                </div>

                {this.state.activeMainTab === 'leaderboard' && (
                    <>
                        <div className='row'>
                            {
                                this.state.boardsDropdownItems.length > 1 ?
                                    <div className="col-lg-3 col-md-6 col-sm-12 ">
                                        <div className="filter-input-label">
                                            Board<span className='text-danger'>*</span>
                                        </div>
                                        <div className={this.props.isCreate && !this.state.selectedBoard ? 'error-border' : ''}>
                                            <Dropdown
                                                placeholder='Select a board'
                                                options={this.state.boardsDropdownItems}
                                                onSelect={(option) => this.onSelectDropDown(option, "selectedBoard")}
                                                text={"title"}
                                                value={this.state.selectedBoard}

                                            />
                                        </div>
                                        {this.props.isCreate && !this.state.selectedBoard ? <div className='error-text text-danger'>Please Select a board</div> : ''}
                                    </div>
                                    : ""
                            }

                            <div className="col-lg-3 col-md-6 col-sm-12 ">
                                <div className="filter-input-label">
                                    Class<span className='text-danger'>*</span>
                                </div>
                                <div className={this.props.isCreate && !this.state.selectedClass ? 'error-border' : ''}>
                                    {!this.state.reloadDropdown ?
                                        <Dropdown
                                            placeholder='Select a Class'
                                            options={this.state.classDropdownItems}
                                            onSelect={(option) => this.onSelectDropDown(option, "selectedClass")}
                                            text={"title"}
                                            value={this.state.selectedClass}
                                        /> : ''}
                                </div>
                                {this.props.isCreate && !this.state.selectedClass ? <div className='error-text text-danger'>Please Select a class</div> : ''}
                            </div>

                            <div className="col-lg-3 col-md-6 col-sm-12 ">
                                <div className="filter-input-label">
                                    Section<span className='text-danger'>*</span>
                                </div>
                                <div className={this.props.isCreate && !this.state.selectedSection ? 'error-border' : ''}>
                                    {
                                        !this.state.reloadSectionDropdown ?
                                            <Dropdown
                                                key="section-multi"
                                                placeholder='Select a Section'
                                                options={this.state.sectionDropdownItems}
                                                onSelect={(option) => this.onSelectDropDown(option, "selectedSection")}
                                                text={"title"}
                                                value={this.state.selectedSection}
                                            >
                                            </Dropdown>
                                            : ""
                                    }
                                </div>
                                {this.props.isCreate && !this.state.selectedSection ? <div className='error-text text-danger'>Please Select a Section</div> : ''}
                            </div>
                        </div>
                        {
                            this.state.showLeaderBoard ?
                                <div className="leaderboard-card mt-4">
                                    <TabBar
                                        activeCategory={activeCategory}
                                        onCategoryChange={this.handleCategoryChange}
                                    />

                                    <LeaderboardHeader
                                        lastUpdated={lastUpdated}
                                        currentMonthDate={currentMonthDate}
                                        onMonthChange={this.handleMonthChange}
                                        isCurrentMonth={isCurrentMonth}
                                        onDownloadReport={this.downloadMeritLeaderboardExcelReport}
                                    />

                                    <ColumnHeader />

                                    <div className={`loading-bar ${isNavigating ? 'active' : ''}`} />

                                    <LeaderboardBody
                                        podium={podium}
                                        others={others}
                                        student={student}
                                        showYouRow={showYouRow}
                                        isLoading={isLoading}
                                        isNavigating={isNavigating}
                                        isError={isError}
                                        onRetry={() => this.fetchClassStudents()}
                                        isCurrentMonth={isCurrentMonth}
                                    />
                                </div>
                                :
                                <div className='text-center mt-5'>Please Select Board, Class, Section to See the Leaderboard</div>
                        }
                    </>
                )}

                {this.state.activeMainTab === 'report' && (
                    <LeaderboardReport type={this.props.type} />
                )}
            </div>
        );
    }
}
